import { useT } from '../../i18n/LanguageContext';
/* global BigInt */
import React, { useEffect, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import { usd, validCents } from './cashMoney';
import BankCalculator from './BankCalculator';
import './CashBank.css';
import { useAuth } from '../../context/AuthContext';
import CashFunding, { canAddCashFunds } from './CashFunding';
const unwrap = (response) => response?.data?.data;
const labels = {
  deposit: 'Funds added',
  order_debit: 'Order charge',
  order_reversal: 'Order reversal',
};
export const validEntries = (value, page) =>
  Array.isArray(value?.rows) &&
  value.rows.length <= 20 &&
  validCents(value.total) &&
  value.total >= value.rows.length &&
  value.page === page &&
  value.limit === 20 &&
  value.rows.every(
    (row) =>
      typeof row.id === 'string' &&
      row.id.length > 0 &&
      labels[row.kind] &&
      Number.isSafeInteger(row.deltaCents) &&
      row.deltaCents !== 0 &&
      (row.kind === 'order_debit' ? row.deltaCents < 0 : row.deltaCents > 0) &&
      [row.productCents, row.shippingCents, row.balanceAfterCents, row.sequence].every(
        validCents
      ) &&
      (row.kind === 'deposit'
        ? row.productCents === 0 && row.shippingCents === 0
        : BigInt(row.productCents) + BigInt(row.shippingCents) ===
          BigInt(Math.abs(row.deltaCents))) &&
      (row.orderNumber === undefined || /^ORD-\d{4}-\d{4,10}$/.test(row.orderNumber)) &&
      Number.isFinite(Date.parse(row.createdAt))
  );
export default function CashBank({ balance, distributorId, onRefresh, readOnly = false }) {
  const t = useT();
  const auth = useAuth();
  const [history, setHistory] = useState(null);
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState('');
  const { begin, version } = useShippingRequest(`cash:${distributorId || 'own'}:${page}`);
  useEffect(() => {
    const request = begin();
    setHistory(null);
    setError('');
    Promise.resolve(distributorService.cashEntries(distributorId, page, { signal: request.signal }))
      .then((entries) => {
        if (!request.isCurrent()) return;
        const rows = unwrap(entries);
        if (!validEntries(rows, page)) throw new Error('Invalid cash history');
        setHistory(rows);
      })
      .catch(() => {
        if (request.isCurrent())
          setError('Cash bank details are unavailable. Retry to load the current records.');
      });
  }, [begin, version, distributorId, page, retry]);
  return (
    <div className="me-cash" aria-label={t('Virtual Tissue Bank Balance')}>
      <fieldset className="me-cash-card me-cash-balance">
        <legend>{t('Virtual Tissue Bank Balance')}</legend>
        <div className="me-cash-heading">
          <p>{t('Shared across your products and customers \xB7 USD')}</p>
          <button type="button" onClick={onRefresh}>
            {t('Refresh balance')}
          </button>
        </div>
        {balance.spendingBlocked && <p>{t('Recorded balance')}</p>}
        <strong>{usd(balance.balanceCents)}</strong>
        {balance.spendingBlocked && (
          <p role="alert">
            {t(
              'Spending is paused. Contact MedEffects to review this account before placing an order.'
            )}
          </p>
        )}
        <p>{t('Orders draw product and shipping charges from this balance.')}</p>
        {!distributorId && !canAddCashFunds(auth?.user, readOnly) && (
          <p className="me-cash-note">
            {t('Adding funds is not available yet. Contact MedEffects before arranging a deposit.')}
          </p>
        )}
      </fieldset>
      {!distributorId && (
        <div id="tissue-bank-funding">
          {canAddCashFunds(auth?.user, readOnly) ? (
            <CashFunding readOnly={readOnly} onFundsChanged={onRefresh} />
          ) : (
            <fieldset className="me-cash-card">
              <legend>{t('Add funds to Tissue Bank')}</legend>
              <p>{t('Credit card funding will be available after setup and verification.')}</p>
              <button type="button" disabled>
                {t('Add funds to Tissue Bank')}
              </button>
            </fieldset>
          )}
        </div>
      )}
      {error && (
        <div role="alert">
          {t(error)}{' '}
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            {t('Retry details')}
          </button>
        </div>
      )}
      {!error && !history && <p role="status">{t('Loading cash bank records\u2026')}</p>}
      <BankCalculator balance={balance} distributorId={distributorId} />
      {history && (
        <fieldset className="me-cash-card">
          <legend>{t('Balance activity')}</legend>
          <div className="me-cash-heading">
            <p>{t('Money added or spent, including order charges, shipping and reversals.')}</p>
            <span>
              {t(history.total === 1 ? '{count} entry' : '{count} entries', {
                count: history.total,
              })}
            </span>
          </div>
          {history.rows.length === 0 ? (
            <p>{t('No cash activity yet.')}</p>
          ) : (
            <div className="me-cash-scroll">
              <table className="me-cash-ledger-table">
                <caption className="sr-only">
                  {t('Cash bank account activity in US dollars')}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('Date')}</th>
                    <th scope="col">{t('Activity')}</th>
                    <th scope="col">{t('Products')}</th>
                    <th scope="col">{t('Shipping')}</th>
                    <th scope="col">{t('Amount')}</th>
                    <th scope="col">{t('Balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.rows.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.createdAt).toLocaleDateString('en-US')}</td>
                      <td>
                        {t(labels[row.kind])}
                        <small>
                          {row.orderNumber ||
                            (row.orderId
                              ? t('Order reference: {reference}', { reference: row.orderId })
                              : row.depositId || '')}
                        </small>
                      </td>
                      <td>{usd(row.productCents)}</td>
                      <td>{usd(row.shippingCents)}</td>
                      <td>
                        {row.deltaCents > 0 ? '+' : ''}
                        {usd(row.deltaCents)}
                      </td>
                      <td>{usd(row.balanceAfterCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {history.total > 20 && (
            <div className="me-cash-actions">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage((value) => value - 1)}
              >
                {t('Previous')}
              </button>
              <span>
                {t('Page {page} of {pages}', { page, pages: Math.ceil(history.total / 20) })}
              </span>
              <button
                type="button"
                disabled={page * 20 >= history.total}
                onClick={() => setPage((value) => value + 1)}
              >
                {t('Next')}
              </button>
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
}
